// Astrea Ops - one format for phone numbers and email addresses (v7.74, 16 Oct 2026).
//
// Martin, 16 Oct 2026: "all emails in the system (vendor or otherwise) should be written in, or
// converted to, all lower caps. Likewise, all telephone numbers should be rewritten to the same
// format xxx-xxx-xxxx throughout, or international numbers +xx-xxx-xxx-xxxx or formatted as
// appropriate for that country." Agreed the same day: numbers and emails inside longer text are
// rewritten too.
//
//   US, Canada and the rest of the North American plan (+1):  714-555-0192
//   Elsewhere: +country code, then the number in that country's usual groups, joined by hyphens:
//     +44-20-7946-0958 (London), +44-7700-900123 (UK mobile), +52-55-1234-5678 (Mexico City),
//     +61-412-345-678 (Australian mobile), +33-1-23-45-67-89 (Paris), +81-3-1234-5678 (Tokyo).
//     A country without its own rule gets groups of three with four at the end: +xx-xxx-xxx-xxxx.
//   Extensions are kept as " x123".
//   Emails: lower case.
// Anything that is not recognisably a phone number is left exactly as typed.
//
// v7.118 (Martin, 28 Oct 2026): "When listing numbers with an international code (like +1) please
// write the number as +xx-xxx-xxx-xxxx or whatever format is normal for a non-US number." A US
// number written with its +1 keeps it, joined by a hyphen: +1-714-555-0192 (without the +1 it stays
// 714-555-0192). A number shown with a separate country code box (a person's cell phone) is shown
// the same way: +1-714-555-0192, +44-20-7946-0958 (phoneWithCode).
//
// Plain functions with no imports, so the app, the tests and the one-time cleanup script
// (scripts/tidy-contacts.mjs) all use the same code.

// ── country calling codes (E.164 codes are prefix-free, so the longest match wins) ─────────
const CC2 = new Set(['20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49',
  '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86',
  '90', '91', '92', '93', '94', '95', '98']);
const CC3 = new Set(['211', '212', '213', '216', '218', '220', '221', '222', '223', '224', '225', '226', '227', '228', '229',
  '230', '231', '232', '233', '234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244', '245', '246',
  '248', '249', '250', '251', '252', '253', '254', '255', '256', '257', '258', '260', '261', '262', '263', '264', '265',
  '266', '267', '268', '269', '290', '291', '297', '298', '299', '350', '351', '352', '353', '354', '355', '356', '357',
  '358', '359', '370', '371', '372', '373', '374', '375', '376', '377', '378', '379', '380', '381', '382', '383', '385',
  '386', '387', '389', '420', '421', '423', '500', '501', '502', '503', '504', '505', '506', '507', '508', '509', '590',
  '591', '592', '593', '594', '595', '596', '597', '598', '599', '670', '672', '673', '674', '675', '676', '677', '678',
  '679', '680', '681', '682', '683', '685', '686', '687', '688', '689', '690', '691', '692', '850', '852', '853', '855',
  '856', '880', '886', '960', '961', '962', '963', '964', '965', '966', '967', '968', '970', '971', '972', '973', '974',
  '975', '976', '977', '992', '993', '994', '995', '996', '998']);

// Country names (as typed in an address) to calling codes, for numbers written without a "+".
const COUNTRY_CC = {
  'usa': '1', 'us': '1', 'u.s.': '1', 'u.s.a.': '1', 'united states': '1', 'united states of america': '1', 'america': '1',
  'canada': '1', 'puerto rico': '1', 'mexico': '52', 'méxico': '52', 'uk': '44', 'u.k.': '44', 'united kingdom': '44',
  'great britain': '44', 'britain': '44', 'england': '44', 'scotland': '44', 'wales': '44', 'northern ireland': '44',
  'ireland': '353', 'australia': '61', 'new zealand': '64', 'japan': '81', 'germany': '49', 'france': '33', 'spain': '34',
  'italy': '39', 'netherlands': '31', 'the netherlands': '31', 'belgium': '32', 'switzerland': '41', 'austria': '43',
  'denmark': '45', 'sweden': '46', 'norway': '47', 'poland': '48', 'portugal': '351', 'greece': '30', 'india': '91',
  'china': '86', 'hong kong': '852', 'singapore': '65', 'south korea': '82', 'korea': '82', 'philippines': '63',
  'thailand': '66', 'malaysia': '60', 'indonesia': '62', 'vietnam': '84', 'brazil': '55', 'argentina': '54', 'chile': '56',
  'colombia': '57', 'peru': '51', 'costa rica': '506', 'panama': '507', 'south africa': '27', 'israel': '972',
  'united arab emirates': '971', 'uae': '971', 'saudi arabia': '966', 'qatar': '974', 'turkey': '90', 'iceland': '354',
  'finland': '358', 'czech republic': '420', 'czechia': '420', 'hungary': '36', 'romania': '40',
};
export function callingCodeForCountry(country) {
  const k = String(country || '').trim().toLowerCase();
  if (!k) return '';
  return COUNTRY_CC[k] || '';
}

function splitCC(d) {
  if (!d) return null;
  if (d[0] === '1' || d[0] === '7') return { cc: d[0], nsn: d.slice(1) };
  if (CC2.has(d.slice(0, 2))) return { cc: d.slice(0, 2), nsn: d.slice(2) };
  if (CC3.has(d.slice(0, 3))) return { cc: d.slice(0, 3), nsn: d.slice(3) };
  return null;
}

// Splits digits into groups of the given sizes; any digits left over join the last group.
function groups(d, sizes) {
  if (sizes.some((x) => !(x > 0))) return generic(d);
  const out = []; let i = 0;
  sizes.forEach((n, j) => { if (i >= d.length) return; out.push(j === sizes.length - 1 ? d.slice(i) : d.slice(i, i + n)); i += n; });
  if (i < d.length && out.length < sizes.length) out.push(d.slice(i));
  return out.filter(Boolean);
}
// Threes, with four at the end where that avoids a lone digit: 10 -> 3-3-4, 9 -> 3-3-3, 8 -> 4-4.
function generic(d) {
  const n = d.length;
  if (n <= 4) return [d];
  if (n === 5) return [d];
  if (n === 6) return groups(d, [3, 3]);
  if (n === 7) return groups(d, [3, 4]);
  if (n === 8) return groups(d, [4, 4]);
  if (n === 9) return groups(d, [3, 3, 3]);
  if (n === 10) return groups(d, [3, 3, 4]);
  if (n === 11) return groups(d, [3, 4, 4]);
  return groups(d, [3, 3, 3, 3, 3]);
}

// Countries whose numbers keep their leading 0 after the country code.
const KEEP_ZERO = new Set(['39', '378', '379', '225', '242']);

// The national number in its country's groups. Returns null when it is clearly not a number
// from that country (wrong length).
function nationalGroups(cc, nsn) {
  let d = nsn;
  if (!KEEP_ZERO.has(cc) && d.length > 4 && d[0] === '0') d = d.slice(1);
  if (cc === '7' && d.length === 11 && d[0] === '8') d = d.slice(1);
  if (cc === '52' && d.length === 11 && d[0] === '1') d = d.slice(1);
  const n = d.length;
  if (n < 4 || n > 13) return null;
  const s2 = d.slice(0, 2), s1 = d[0];
  switch (cc) {
    case '44': // United Kingdom
      if (n < 9 || n > 10) return null;
      if (s2 === '20' || s2 === '23' || s2 === '24' || s2 === '28' || s2 === '29') return groups(d, [2, 4, 4]);
      if (s1 === '7') return groups(d, [4, 6]);
      if (/^1(1\d|\d1)/.test(d)) return groups(d, [3, 3, 4]);
      if (/^(3|8|9)/.test(d)) return groups(d, [3, 3, 4]);
      return groups(d, [4, n - 4]);
    case '353': // Ireland
      if (s1 === '1') return groups(d, [1, 3, n - 4]);
      return groups(d, [2, 3, n - 5]);
    case '61': // Australia
      if (n !== 9) return null;
      return s1 === '4' || s1 === '5' ? groups(d, [3, 3, 3]) : groups(d, [1, 4, 4]);
    case '64': // New Zealand
      if (s1 === '2') return groups(d, [2, 3, n - 5]);
      return groups(d, [1, 3, n - 4]);
    case '52': // Mexico
      if (n !== 10) return null;
      return ['55', '33', '81'].includes(s2) ? groups(d, [2, 4, 4]) : groups(d, [3, 3, 4]);
    case '33': // France
      if (n !== 9) return null;
      return groups(d, [1, 2, 2, 2, 2]);
    case '34': case '351': case '48': // Spain, Portugal, Poland
      if (n !== 9) return null;
      return groups(d, [3, 3, 3]);
    case '39': // Italy (keeps its 0)
      if (s1 === '3') return groups(d, [3, 3, n - 6]);
      if (s2 === '02' || s2 === '06') return groups(d, [2, 4, n - 6]);
      return generic(d);
    case '49': // Germany
      if (/^1[5-7]/.test(d)) return groups(d, [3, 4, n - 7]);
      if (['30', '40', '69', '89'].includes(s2)) return groups(d, [2, 4, n - 6]);
      return groups(d, [3, 4, n - 7]);
    case '31': // Netherlands
      if (n !== 9) return null;
      if (s1 === '6') return groups(d, [1, 4, 4]);
      if (['10', '20', '30', '40', '70'].includes(s2)) return groups(d, [2, 3, 4]);
      return groups(d, [3, 3, 3]);
    case '41': // Switzerland
      if (n !== 9) return null;
      return groups(d, [2, 3, 2, 2]);
    case '45': // Denmark
      if (n !== 8) return null;
      return groups(d, [2, 2, 2, 2]);
    case '47': // Norway
      if (n !== 8) return null;
      return groups(d, [3, 2, 3]);
    case '81': // Japan
      if (s1 === '3' || s1 === '6') return groups(d, [1, 4, n - 5]);
      return groups(d, [2, 4, n - 6]);
    case '82': // South Korea
      if (s1 === '2') return groups(d, [1, n - 5, 4]);
      return groups(d, [2, n - 6, 4]);
    case '86': // China
      if (s1 === '1' && n === 11) return groups(d, [3, 4, 4]);
      if (s2 === '10' || s1 === '2') return groups(d, [2, 4, n - 6]);
      return groups(d, [3, 4, n - 7]);
    case '91': // India
      if (n !== 10) return null;
      return groups(d, [5, 5]);
    case '65': case '852': case '853': // Singapore, Hong Kong, Macau
      if (n !== 8) return null;
      return groups(d, [4, 4]);
    case '971': case '966': case '972': // UAE, Saudi Arabia, Israel
      if (s1 === '5') return groups(d, [2, 3, n - 5]);
      return groups(d, [1, 3, n - 4]);
    case '27': // South Africa
      if (n !== 9) return null;
      return groups(d, [2, 3, 4]);
    case '55': // Brazil
      return groups(d, [2, n - 6, 4]);
    case '63': // Philippines
      if (s1 === '9' && n === 10) return groups(d, [3, 3, 4]);
      return generic(d);
    case '90': case '7': // Turkey, Russia
      if (n !== 10) return null;
      return groups(d, [3, 3, 2, 2]);
    default:
      return generic(d);
  }
}

const isNanp = (d) => d.length === 10 && /^[2-9]\d{2}[2-9]/.test(d);
const nanp = (d) => d.slice(0, 3) + '-' + d.slice(3, 6) + '-' + d.slice(6);

const EXT_RE = /\s*[,;]?\s*(?:ext\.?|extension|x|#)\s*(\d{1,6})\s*$/i;

// One number in our format, or null when it cannot be read as a phone number.
//   opts.cc       calling code that goes with it ("+44", "44"), e.g. a person's country code box
//   opts.country  the country of the address it belongs to ("UK", "Mexico"), for national numbers
//   opts.national true: return only the national part (for fields with their own country code box)
export function phoneOrNull(raw, opts = {}) {
  let s = String(raw ?? '').trim();
  if (!s) return null;
  let ext = '';
  const em = s.match(EXT_RE);
  if (em) { ext = ' x' + em[1]; s = s.slice(0, em.index).trim(); }
  if (/[A-Za-z]/.test(s)) return null;
  s = s.replace(/\(0\)/g, '');
  const intl = /^\s*(\+|00|011)/.exec(s);
  let d = s.replace(/\D/g, '');
  const fixedCC = String(opts.cc || '').replace(/\D/g, '') || callingCodeForCountry(opts.country);
  let cc, nsn;
  if (intl) {
    d = d.slice(intl[1] === '+' ? 0 : intl[1].length);
    const sp = splitCC(d);
    if (!sp) return null;
    ({ cc, nsn } = sp);
  } else if (fixedCC && fixedCC !== '1') {
    if (d.startsWith(fixedCC) && d.length > fixedCC.length + 6 && !d.startsWith('0')) d = d.slice(fixedCC.length);
    cc = fixedCC; nsn = d;
  } else {
    if (d.length === 11 && d[0] === '1') d = d.slice(1);
    if (!isNanp(d)) return null;
    cc = '1'; nsn = d;
  }
  if (cc === '1') {
    if (nsn.length === 11 && nsn[0] === '1') nsn = nsn.slice(1);
    if (!isNanp(nsn)) return null;
    return (intl && !opts.national ? '+1-' : '') + nanp(nsn) + ext;
  }
  const g = nationalGroups(cc, nsn);
  if (!g) return null;
  const nat = g.join('-');
  return (opts.national ? nat : '+' + cc + '-' + nat) + ext;
}

// A number with its country code, for showing: the code box and the number joined by a hyphen
// (+1-714-555-0192, +44-20-7946-0958). A number typed with its own code keeps that code. Anything
// that cannot be read as a phone number is shown as typed, after the code.
export function phoneWithCode(raw, cc) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (/^(\+|00|011)/.test(s)) return phoneOrNull(s) || s;
  const code = String(cc ?? '').replace(/\D/g, '') || '1';
  const nat = phoneOrNull(s, { cc: code, national: true });
  if (!nat) return '+' + code + ' ' + s;
  return '+' + code + '-' + nat;
}

export function normalizeEmail(raw) {
  return String(raw ?? '').trim().toLowerCase();
}

const EMAIL_G = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g;
// "+44 20 7946 0958", "+1 (714) 555-0192", "+52 55 1234 5678"
const INTL_G = /\+\s?\d{1,3}(?:[\s.\-]*(?:\(0\)|\(\d{1,4}\)|\d)){4,15}/g;
// (714) 555-0192, 714-555-0192, 714.555.0192, 714 555 0192, 1-800-555-1212. A separator or
// brackets are required, so a bare ten-digit reference number is never touched.
const NANP_G = /(^|[^\w+$.\/-])((?:1[\s.-])?(?:\(\d{3}\)\s?|\d{3}[\s.-])\d{3}[\s.-]\d{4})(?![\w-]|\.\d)/g;

// Rewrites every email and recognisable phone number inside a piece of text.
export function tidyText(raw) {
  if (typeof raw !== 'string' || !raw) return raw;
  let s = raw.replace(EMAIL_G, (m) => m.toLowerCase());
  s = s.replace(INTL_G, (m) => {
    const trail = m.match(/[\s.\-]+$/);
    const core = trail ? m.slice(0, -trail[0].length) : m;
    const f = phoneOrNull(core);
    return f ? f + (trail ? trail[0] : '') : m;
  });
  s = s.replace(NANP_G, (all, pre, num, offset, whole) => {
    const before = whole.slice(Math.max(0, offset + pre.length - 6), offset + pre.length);
    if (/\+\d{1,3}[\s.-]?$/.test(before)) return all;
    const f = phoneOrNull(num);
    return f ? pre + f : all;
  });
  return s;
}

// A field that should hold one phone number: formatted when it reads as one number, otherwise
// any numbers and emails inside it are tidied and the rest is left as typed.
export function formatPhoneField(raw, opts = {}) {
  if (raw == null) return raw;
  const s = String(raw);
  if (!s.trim()) return s.trim();
  return phoneOrNull(s, opts) ?? tidyText(s.trim());
}

// Every string in a flat object (an Event Form, a client form): emails and phone numbers tidied.
// Keys listed in skip are left alone. Returns the same object when nothing changed.
export function tidyFields(obj, skip = []) {
  if (!obj || typeof obj !== 'object') return obj;
  let out = obj;
  Object.keys(obj).forEach((k) => {
    if (skip.includes(k)) return;
    const v = obj[k];
    if (typeof v !== 'string' || !v) return;
    const t = /email$/i.test(k) ? normalizeEmail(v) : /phone$/i.test(k) ? formatPhoneField(v) : tidyText(v);
    if (t !== v) { if (out === obj) out = { ...obj }; out[k] = t; }
  });
  return out;
}
